import React from 'react'
import { Section, GuideHeader, P, UI, Steps, Bullets, Callout } from './GuideParts'

export default function OutputDestinationsGuide() {
  return (
    <div className="space-y-8">
      <GuideHeader
        title="Output Destinations"
        intro="Deliver approved results as files or database rows, automatically."
      />

      <Section number={1} title="What Output Destinations Are">
        <P>
          When a request is approved and published, Xtract AI can send the result on. A <UI>Connector</UI> sends it to a web address
          (webhook). An <UI>Output Destination</UI> delivers it to storage you own. There are two kinds:
        </P>
        <Bullets>
          <li><span className="font-semibold">FTP / SFTP file export:</span> a CSV file is written to a folder on your server.</li>
          <li><span className="font-semibold">Azure SQL table:</span> approved values are written to a table in your own Azure SQL database.</li>
        </Bullets>
        <P>Only Admins manage destinations, and the page only appears when Connectors are switched on for your organisation.</P>
      </Section>

      <Section number={2} title="Adding an FTP / SFTP Destination">
        <Steps>
          <li>Open <UI>Output Destinations</UI> and choose <UI>Add Destination</UI>.</li>
          <li>Pick <UI>FTP / SFTP file export</UI>, give it a name such as “Finance SFTP Drop”, and choose the protocol: SFTP (SSH File Transfer Protocol) or FTP (File Transfer Protocol).</li>
          <li>Enter the host, port, folder and credentials. The port defaults to 22 for SFTP and 21 for FTP but you can change it.</li>
          <li>Set the file name pattern and the field mapping for the CSV columns.</li>
          <li>Choose <UI>Test connection</UI>, then save.</li>
        </Steps>
        <Callout kind="info">
          The first time an SFTP server is contacted its host key is remembered, so later connections are checked against it.
        </Callout>
      </Section>

      <Section number={3} title="Adding an Azure SQL Destination">
        <P>
          Choose <UI>Azure SQL table</UI> instead. Create the tables in your database first, then enter the connection details and use
          <UI>Test connection</UI> to check them before saving. Approved results are written to that table.
        </P>
      </Section>

      <Section number={4} title="Linking a Destination to a Template">
        <P>
          A destination does nothing until a template uses it. Open the template, go to its <UI>Output Destinations</UI> tab and choose the
          destination. From then on, every approved request on that template is delivered there.
        </P>
      </Section>

      <Section number={5} title="Delivery History and Re-export">
        <P>
          Each destination has a <UI>Delivery History</UI> showing every file or write, with its generated file name and whether it
          succeeded. If a delivery failed, fix the cause (for example the credentials) and re-export it from the request’s
          Exports tab. Re-export is available to Admins.
        </P>
      </Section>
    </div>
  )
}
